import { ReactNode } from "react";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import LockOutlined from "@mui/icons-material/LockOutlined";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../../../hooks/useTranslation";
import { useAdminAccess } from "../../../hooks/useAdminAccess";
import {
  ADMIN_PAGES,
  AdminPageKey,
  allowedAdminMenuPages,
} from "../../../utils/adminAccess";

type Props = { page: AdminPageKey; children: ReactNode };

// Wraps an admin route: without the page's role (e.g. a link typed or shared)
// it explains which role is missing and offers a page the user may open,
// instead of a bare "no access" or a silent redirect. The pages keep their own
// checks and the backend enforces the roles either way.
export const AdminPageGuard = ({ page, children }: Props) => {
  const { translate } = useTranslation("pages.admin.guard");
  const access = useAdminAccess();
  const navigate = useNavigate();

  if (ADMIN_PAGES[page].allowed(access)) {
    return <>{children}</>;
  }

  const [home] = allowedAdminMenuPages(access);

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: "10px",
        p: { xs: 3, md: 5 },
        maxWidth: 560,
        mx: "auto",
      }}
    >
      <Stack gap={2} alignItems="center" textAlign="center">
        <LockOutlined sx={{ fontSize: 44, color: "text.disabled" }} />
        <Typography variant="h6">{translate("title")}</Typography>
        <Typography variant="body2" color="text.secondary">
          {translate("message")}
        </Typography>
        <Box
          component="code"
          sx={{
            px: 1,
            py: 0.5,
            borderRadius: 1,
            fontSize: 13,
            bgcolor: "action.hover",
          }}
        >
          {ADMIN_PAGES[page].role}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {translate("hint")}
        </Typography>
        {home && (
          <Button
            variant="contained"
            onClick={() => navigate(ADMIN_PAGES[home].path)}
          >
            {translate("goToAllowed")}
          </Button>
        )}
      </Stack>
    </Paper>
  );
};
